import React, { useState, useRef } from 'react'; // eslint-disable-line no-unused-vars
import Webcam from 'react-webcam';
import {
    Dialog,
    DialogActions,
    DialogTitle,
    DialogContent,
} from '@material-ui/core';
import { decode } from 'base64-arraybuffer';
import { uploadImageToS3 } from '../../../API/api';
import { extractExtention } from '../../../utility/utility';
import errorHandler from '../../../utility/errorHandler/errorHandler';
import SuccessAlert from '../../../utility/successAlert/successAlert';

const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: 'environment',
};

const FrontlineCommentModal = ({
    isOpen,
    onSkip,
    areaId,
    frontlineTaskId,
    getFrontlineCommentPresignUrl,
    submitFrontlineComment,
}) => {
    const [comment, setComment] = useState('');
    const [uploadedAttachments, setUploadedAttachments] = useState([]);
    const [previewImages, setPreviewImages] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showCamera, setShowCamera] = useState(false);
    const [lightboxImage, setLightboxImage] = useState(null);
    const webcamRef = useRef(null);
    const fileInputRef = useRef(null);

    const isSubmitEnabled =
        !isUploading && !isSubmitting && comment.trim().length > 0 && uploadedAttachments.length > 0;

    const uploadImages = async (base64Array, imageNames) => {
        setIsUploading(true);
        try {
            const extensions = extractExtention(imageNames);
            const res = await getFrontlineCommentPresignUrl(areaId, extensions);
            const presignedUrls = res?.value?.data?.presigned_urls;

            const newAttachments = [];
            const newPreviews = [];

            for (let i = 0; i < presignedUrls.length; i++) {
                const { presigned_url, attachment_path } = presignedUrls[i];
                const base64Data = base64Array[i].split(',')[1];
                const arrayBuffer = decode(base64Data);
                await uploadImageToS3(presigned_url, arrayBuffer);
                newAttachments.push({ photo_path: attachment_path });
                newPreviews.push(base64Array[i]);
            }

            setUploadedAttachments((prev) => [...prev, ...newAttachments]);
            setPreviewImages((prev) => [...prev, ...newPreviews]);
        } catch (err) {
            errorHandler('Failed to upload image. Please try again.');
        } finally {
            setIsUploading(false);
        }
    };

    const onFileChange = (e) => {
        const files = Array.from(e.target.files); // snapshot before clearing — FileList is live
        const inputEl = e.target;
        if (!files.length) return;

        inputEl.value = '';

        const base64Array = [];
        const imageNames = [];

        const readNext = (index) => {
            if (index >= files.length) {
                uploadImages(base64Array, imageNames);
                return;
            }
            const file = files[index];
            imageNames.push(file.name);
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                base64Array.push(reader.result);
                readNext(index + 1);
            };
            reader.onerror = () => {
                errorHandler('Failed to read file. Please try again.');
            };
        };

        readNext(0);
    };

    const capture = async () => {
        const imageSrc = webcamRef.current?.getScreenshot();
        setShowCamera(false);
        const imageName = `capture_${uploadedAttachments.length}.jpg`;
        await uploadImages([imageSrc], [imageName]);
    };

    const removeAttachment = (index) => {
        setUploadedAttachments((prev) => prev.filter((_, i) => i !== index));
        setPreviewImages((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = () => {
        const feedback = { frontline_task_id: frontlineTaskId };
        if (comment.trim()) feedback.comment = comment.trim();
        if (uploadedAttachments.length > 0) {
            feedback.attachments_attributes = [...uploadedAttachments];
        }

        setIsSubmitting(true);
        submitFrontlineComment({ feedback }, areaId)
            .then(() => {
                setIsSubmitting(false);
                SuccessAlert('FeedBack submitted successfully');
                setTimeout(() => onSkip(), 1500);
            })
            .catch(() => {
                setIsSubmitting(false);
                errorHandler('Failed to submit comment. Please try again.');
            });
    };

    return (
        <>
            <Dialog
                open={isOpen}
                aria-labelledby="frontline-comment-dialog"
                maxWidth="xs"
                fullWidth
                onClose={(_, reason) => {
                    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                        onSkip();
                    }
                }}
            >
                <DialogTitle id="frontline-comment-dialog">
                    <h3 className="fcm-dialog-title text-center">Leave a Comment</h3>
                </DialogTitle>
                <DialogContent>
                    <div className="comments_wrap">
                        <div className="form-group">
                            <label className="form_title">Please provide any comments about the Area</label>
                            <textarea
                                className="form-control textarea_modifier"
                                rows="4"
                                placeholder="Add a comment..."
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                            />
                        </div>
                        <div className="form-group btn_block mb-0">
                            <button
                                type="button"
                                className="w-100 py-3 mb-3 fcm-camera-btn"
                                onClick={() => setShowCamera(true)}
                                disabled={isUploading}
                            >
                                <i className="fa fa-camera" aria-hidden="true"></i>  Take a Photo and upload it
                            </button>
                            <div className="file_attachment">
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    className="fcm-file-input"
                                    multiple
                                    accept="image/*"
                                    onChange={onFileChange}
                                />
                                <button
                                    type="button"
                                    onClick={() => !isUploading && fileInputRef.current?.click()}
                                    className="file_title fcm-upload-btn"
                                >
                                    <img src="/images/icons/attachment_icon.png" alt="Icon" /> Upload attachments
                                </button>
                                {isUploading && (
                                    <span className="ml-2">
                                        <small className="fcm-uploading-text">Uploading...</small>
                                    </span>
                                )}
                                {previewImages.length > 0 && (
                                    <div className="upload_file fcm-upload-file">
                                        <div className="row fcm-preview-row">
                                            {previewImages.map((url, i) => (
                                                <div className="col-4 col-sm-4 cst_gutter" key={i}>
                                                    <div
                                                        className="file_view fcm-file-view-clickable"
                                                        onClick={() => setLightboxImage(url)}
                                                    >
                                                        <img
                                                            src={url}
                                                            alt={`attachment ${i + 1}`}
                                                            className="fcm-thumbnail-img"
                                                        />
                                                    </div>
                                                    <div className="cancel_icon">
                                                        <img
                                                            src="/images/icons/icn_delete_white.png"
                                                            onClick={(e) => { e.stopPropagation(); removeAttachment(i); }}
                                                            alt="Remove"
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <button
                        type="button"
                        className={`btn cst_btn btn_danger fcm-action-btn${isSubmitting ? ' btn-wait' : ''}`}
                        disabled={!isSubmitEnabled}
                        onClick={handleSubmit}
                    >
                        {isSubmitting ? '' : 'Submit'}
                    </button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!lightboxImage}
                onClose={() => setLightboxImage(null)}
                aria-labelledby="frontline-lightbox-dialog"
                maxWidth="md"
                fullWidth
            >
                <DialogTitle id="frontline-lightbox-dialog" className="fcm-lightbox-title">
                    <div className="fcm-lightbox-close-wrapper">
                        <button
                            type="button"
                            onClick={() => setLightboxImage(null)}
                            className="fcm-lightbox-close-btn"
                            aria-label="Close"
                        >
                            &times;
                        </button>
                    </div>
                </DialogTitle>
                <DialogContent className="fcm-lightbox-content">
                    {lightboxImage && (
                        <img
                            src={lightboxImage}
                            alt="Full size preview"
                            className="fcm-lightbox-img"
                        />
                    )}
                </DialogContent>
            </Dialog>

            <Dialog
                open={showCamera}
                onClose={() => setShowCamera(false)}
                aria-labelledby="frontline-camera-dialog"
            >
                <DialogTitle id="frontline-camera-dialog">
                    <h3 className="fcm-dialog-title text-center">Capture Image</h3>
                </DialogTitle>
                <DialogContent className="pl-5 pr-5">
                    <Webcam
                        audio={false}
                        height={200}
                        ref={webcamRef}
                        screenshotFormat="image/jpeg"
                        width="100%"
                        videoConstraints={videoConstraints}
                    />
                </DialogContent>
                <DialogActions>
                    <button
                        className="btn cst_btn btn_danger"
                        onClick={capture}
                    >
                        Capture
                    </button>
                    <button
                        className="btn cst_btn btn-outline-secondary fcm-action-btn"
                        onClick={() => setShowCamera(false)}
                        type="button"
                    >
                        Cancel
                    </button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default FrontlineCommentModal;
